import { inject, Injectable } from '@angular/core';
import { SupabaseDatabaseService } from './supabase-database.service';
import TYPES from '../constants/types';
import { EdgeFunctionCallerService } from './edge-function-caller.service';
import { TranslateService } from '@ngx-translate/core';
import { UserPsychologist } from '../models/user-psychologist';
import { UserPsychologistService } from './user-psychologist.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root',
})
export class InviteService<Invite> extends SupabaseDatabaseService<Invite> {
  
  edgeFunctionCallerService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService);
  userPsychologistService: UserPsychologistService<UserPsychologist> = inject(UserPsychologistService<UserPsychologist>);
  translate: TranslateService = inject(TranslateService);
  snackBar: MatSnackBar = inject(MatSnackBar);

  async getByToken(token: string): Promise<Invite>{
    const { data, error } = await this.supabase
    .from(TYPES.DATABASE.TABLES.INVITES)
    .select(`
      *,
      psychologists(
        id,
        first_name,
        last_name,
        phone_number
      )
    `)  
    .eq('token', token.trim())    
    .maybeSingle();

    if (error) {
      console.error(`Erro ao buscar Invite com o token ${token} em '${TYPES.DATABASE.TABLES.INVITES}':`,
        error);
      throw new Error(error.message);
    }
    return (data as Invite);
  }

  async getByTokenAndEmail(token: string, email: string): Promise<Invite>{
    const { data, error } = await this.supabase
    .from(TYPES.DATABASE.TABLES.INVITES)
    .select(`
      *,
      psychologists(
        id,
        user_id,
        first_name,
        last_name,
        phone_number
      )
    `)  
    .eq('token', token.trim())    
    .eq('email', email.trim())
    .maybeSingle();

    if (error) {
      console.error(`Erro ao buscar Invite com o token ${token} e email ${email} em '${TYPES.DATABASE.TABLES.INVITES}':`,
        error);
      throw new Error(error.message);
    }
    return (data as Invite);
  }

  async getPendingByPsychologistId(psychologistId: string): Promise<Invite[]>{
    const { data, error } = await this.supabase
    .from(TYPES.DATABASE.TABLES.INVITES)
    .select(`
      *      
    `)
    .eq('status', TYPES.DATABASE.STATUS.PENDING.code)  
    .eq('psychologist_id', psychologistId);

    if (error) {
      console.error(`Erro ao buscar Invite com o psychologist_id ${psychologistId} em '${TYPES.DATABASE.TABLES.INVITES}':`,
        error);
      throw new Error(error.message);
    }
    return (data as Invite[]) || [];
  }

  async inviteAccepted(inviteId: string, userId: string, psychologistId: string, psychologistUserId: string){
    
    await this.userPsychologistService.create(
      TYPES.DATABASE.TABLES.USERS_PSYCHOLOGISTS,
      {
        user_id: userId,
        psychologist_id: psychologistId,
        status: TYPES.DATABASE.STATUS.ACTIVE.code
      }
    );

    await this.update(TYPES.DATABASE.TABLES.INVITES, inviteId, {status: TYPES.DATABASE.STATUS.ACCEPTED.code});

    const requestUserId = psychologistUserId;
    if(requestUserId){
      this.edgeFunctionCallerService.addPushNotification(
        requestUserId, 
        new Date().toISOString(),
        this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_APPROVED.title),
        this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_APPROVED.body),
        {
          onActionClick: {
            default: {operation: "navigateLastFocusedOrOpen", url: "/home"}
          }
        }
      );
    }

    const message:string = this.translate.instant('info.invite_accepted');
    const action:string = this.translate.instant('buttons.dismiss');
    this.snackBar.open(message, action);

  }

}
